/**
 * TaskTypeIcons — a single, shared set of premium line-art SVG icons for the
 * three FederateX task families. Used everywhere a task type is shown so the
 * visual language stays consistent across DataHub, Learning Models, Training
 * Setup, the federation map, and dataset/model cards.
 *
 *   tabular               → a structured data table (rows + columns)
 *   imaging_classification → an image with a single whole-image label
 *   imaging_segmentation   → an image with a dashed region mask (pixel contour)
 *
 * Style notes
 * -----------
 * Stroke-based, 24-grid, `currentColor` so each icon inherits its surrounding
 * accent. Sized at `1em` by default so they behave exactly like MUI/SvgIcon
 * components under an `'& svg': { fontSize }` wrapper. Pass `size={px}` when a
 * container does not set a font-size (e.g. plain flex boxes, MUI Chip icons).
 */

const baseProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  xmlns: "http://www.w3.org/2000/svg",
};

const sized = (size) =>
  size != null ? { width: size, height: size } : { width: "1em", height: "1em" };

/** Tabular — structured records laid out in a table grid. */
export const TabularIcon = ({ size, ...props }) => (
  <svg {...baseProps} {...sized(size)} {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2.2" />
    {/* header row + body rows */}
    <path d="M3 9h18" />
    <path d="M3 14.5h18" />
    {/* columns */}
    <path d="M9 4v16" />
    <path d="M15 4v16" />
  </svg>
);

/** Imaging · Classification — an image tagged with one whole-image label. */
export const ImagingClassificationIcon = ({ size, ...props }) => (
  <svg {...baseProps} {...sized(size)} {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2.4" />
    {/* picture: sun + ridge line */}
    <circle cx="7.6" cy="8.3" r="1.4" />
    <path d="M3.6 14.4l3-3.2 2.1 1.9 2.7-3 3.2 3.5" />
    {/* label strip with a single caption/label line */}
    <path d="M3 15.6h18" />
    <path d="M6.2 17.9h7.4" />
  </svg>
);

/** Imaging · Segmentation — an image with a dashed pixel-level region mask. */
export const ImagingSegmentationIcon = ({ size, ...props }) => (
  <svg {...baseProps} {...sized(size)} {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2.4" />
    {/* image context: sun in the corner */}
    <circle cx="6.7" cy="7.6" r="1.2" />
    {/* dashed segmentation contour (the mask) */}
    <path
      d="M12.9 7.7c3.1-0.5 5.2 1.7 4.7 4.2-0.45 2.3-2.9 3.7-5.2 3-1.9-0.6-3.2-2.4-2.8-4.3 0.32-1.55 1.6-2.6 3.3-2.9z"
      strokeWidth={1.6}
      strokeDasharray="2.3 1.7"
    />
  </svg>
);

/** Resolve an icon component from a task / processing-type key. */
export const TASK_TYPE_ICON = {
  tabular: TabularIcon,
  imaging: ImagingClassificationIcon,
  imaging_classification: ImagingClassificationIcon,
  classification: ImagingClassificationIcon,
  segmentation: ImagingSegmentationIcon,
  imaging_segmentation: ImagingSegmentationIcon,
};

export const getTaskTypeIcon = (key) => TASK_TYPE_ICON[key] || null;

export default TASK_TYPE_ICON;
